/**
 * Per-section diff view (old → subsequent → current) for two versions.
 *
 * Renders the backend's section diff (Phase 7 `/diff`, word-level granularity
 * since BLA-362/ADR-077): each section is classified added / removed /
 * modified / unchanged, with a **word-level** colored diff for changed
 * sections — a single-word edit no longer highlights the whole surrounding
 * line. This is the same diff machine that powers ingestion review AND the
 * save-version review dialog, so the provider sees changes the same way
 * everywhere. No content is logged (HIPAA).
 *
 * The `frontmatter` section is intentionally excluded from this component —
 * its proposed value is rendered by {@link FrontmatterEditor} in edit mode
 * (see ProposalDiffReview).
 *
 * Body text can carry inline `[[src:...]]` grounding citations (the same
 * markup Blueprint card bodies use); every rendered run routes through
 * {@link renderInline} — the same citation-aware inline parser `Markdown` uses
 * — so a diff never shows the literal `[[src:...]]` marker, only the clean
 * dagger + hover/click citation affordance. The backend tokenizer treats a
 * `[[...]]` marker as one atomic token, so it can never be split mid-marker
 * by the word-level diff.
 *
 * **Accessibility (not color alone):** an inserted run is underlined in
 * addition to its green background, and a deleted run is struck through in
 * addition to its red background — so additions/deletions are distinguishable
 * for a color-blind or grayscale viewer, not just by hue.
 */
import React from "react";
import { renderInline } from "../blueprint/inlineCitation";
import type {
  DiffGranularity,
  SectionChangeKind,
  SectionDiff,
  VersionDiff,
  WordDiffOp,
} from "../../types";

/** Tailwind accent per change kind. */
const CHANGE_BADGE: Record<SectionChangeKind, string> = {
  added: "bg-emerald-100 text-emerald-700",
  removed: "bg-rose-100 text-rose-700",
  modified: "bg-amber-100 text-amber-700",
  unchanged: "bg-slate-100 text-slate-500",
};

/** Props for {@link DiffView}. */
interface DiffViewProps {
  /** The diff to render, or null while none is selected/loaded. */
  diff: VersionDiff | null;
  /**
   * Optional `{ "<source id>": "<title>" }` lookup so an inline `[[src:...]]`
   * citation resolves to its human source title; absent citations still
   * degrade to the neutral "Source · lines a–b" label (never the raw id).
   */
  sourceTitles?: Record<string, string> | null;
}

/**
 * Coerce a diff side to renderable text.
 *
 * The backend diff contract is string-valued, but a render component must never
 * hard-crash the chat (ErrorBoundary) if a non-string ever slips through (e.g. a
 * structured entity frontmatter object) — React cannot render an object child.
 * A non-string is shown as indented JSON.
 */
function asText(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
}

/** Tailwind classes per word-diff run kind — color PLUS a non-color signal. */
const WORD_OP_CLASS: Record<WordDiffOp["kind"], string> = {
  equal: "",
  // Underline in addition to the green background — an addition must be
  // legible without relying on hue alone (a11y floor, B2).
  insert: "bg-emerald-100 text-emerald-900 underline decoration-emerald-600",
  // Strikethrough in addition to the red background — same rationale for a
  // deletion, using a distinct decoration so insert/delete are never confused
  // by shape alone either.
  delete: "bg-rose-100 text-rose-900 line-through decoration-rose-600",
};

/**
 * Render one word-diff run.
 *
 * Citations render through {@link renderInline} same as elsewhere — a run's
 * text can be a whole, unsplit `[[src:...]]` marker (the backend tokenizer
 * never fractures one), so this stays correct even for a cited run.
 */
function WordOpSpan({
  op,
  sourceTitles,
}: {
  op: WordDiffOp;
  sourceTitles?: Record<string, string>;
}): React.ReactElement {
  return (
    <span
      data-testid={`word-diff-op-${op.kind}`}
      className={WORD_OP_CLASS[op.kind]}
    >
      {renderInline(op.text, sourceTitles)}
    </span>
  );
}

/**
 * Tell the provider when the diff they are reading is coarser than word-level.
 *
 * A single changed span too large to refine word-by-word is shown as whole
 * lines instead (ADR-077, edit budget exceeded). Every change is still
 * shown — but a reader who assumes word granularity would over-read a
 * highlighted line as "all of this changed", so the degrade is stated
 * rather than left to be inferred.
 */
function GranularityNote({
  granularity,
}: {
  granularity: DiffGranularity;
}): React.ReactElement | null {
  if (granularity === "word") return null;
  return (
    <p
      data-testid="diff-granularity-note"
      className="mt-2 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
    >
      This change is too large to highlight word by word, so it is shown a whole
      line at a time. Every change is shown.
    </p>
  );
}

/**
 * A render item inside a modified section's word_diff:
 * either an equal run (not a hunk) or a hunk consisting of one or more non-equal ops.
 */
type WordDiffRenderItem =
  | { kind: "equal"; op: WordDiffOp }
  | { kind: "hunk"; hunkIndex: number; ops: WordDiffOp[] };

/**
 * Group word_diff ops into equal runs and contiguous non-equal hunks.
 */
function groupWordDiffOps(
  ops: WordDiffOp[],
  startHunkIndex: number,
): { items: WordDiffRenderItem[]; nextHunkIndex: number } {
  const items: WordDiffRenderItem[] = [];
  let currentHunkOps: WordDiffOp[] = [];
  let hunkIndex = startHunkIndex;

  for (const op of ops) {
    if (op.kind === "equal") {
      if (currentHunkOps.length > 0) {
        items.push({
          kind: "hunk",
          hunkIndex: hunkIndex++,
          ops: currentHunkOps,
        });
        currentHunkOps = [];
      }
      items.push({ kind: "equal", op });
    } else {
      currentHunkOps.push(op);
    }
  }

  if (currentHunkOps.length > 0) {
    items.push({ kind: "hunk", hunkIndex: hunkIndex++, ops: currentHunkOps });
  }

  return { items, nextHunkIndex: hunkIndex };
}

/**
 * Count the number of hunks in a single section.
 */
function countSectionHunks(s: SectionDiff): number {
  if (s.change === "added" || s.change === "removed") {
    return 1;
  }
  if (s.change === "modified") {
    if (s.word_diff.length === 0) {
      return 1;
    }
    let count = 0;
    let inHunk = false;
    for (const op of s.word_diff) {
      if (op.kind !== "equal") {
        if (!inHunk) {
          count += 1;
          inHunk = true;
        }
      } else {
        inHunk = false;
      }
    }
    return count;
  }
  return 0;
}

/**
 * The diff runs for one section (BLA-362/B2).
 *
 * For `modified` sections with a non-empty `word_diff`: renders each run
 * inline, preserving whitespace/paragraph structure (the tokenizer keeps
 * newlines inside its runs) via `whitespace-pre-wrap`. A run is a word on a
 * normally-sized section and a whole line on an oversized one — see
 * {@link GranularityNote}.
 * For `added` sections: renders the full `new` text in solid green.
 * For `removed` sections: renders the full `old` text in solid red.
 * `unchanged` sections render nothing.
 */
function WordDiffView({
  change,
  oldText,
  newText,
  wordDiff,
  granularity,
  sourceTitles,
  startHunkIndex,
}: {
  change: SectionChangeKind;
  oldText: unknown;
  newText: unknown;
  wordDiff: WordDiffOp[];
  granularity: DiffGranularity;
  sourceTitles?: Record<string, string>;
  startHunkIndex: number;
}): React.ReactElement | null {
  if (change === "unchanged") return null;

  if (change === "added") {
    return (
      <pre
        className="text-xs bg-emerald-50 text-slate-700 rounded p-2 mt-2 whitespace-pre-wrap break-words focus:outline-none focus:ring-2 focus:ring-blue-500 scroll-mt-16"
        data-testid="diff-hunk"
        data-diff-hunk={startHunkIndex}
        tabIndex={-1}
      >
        <span data-testid="unified-diff-added">
          {renderInline(asText(newText) || "(empty)", sourceTitles)}
        </span>
      </pre>
    );
  }

  if (change === "removed") {
    return (
      <pre
        className="text-xs bg-rose-50 text-slate-700 rounded p-2 mt-2 whitespace-pre-wrap break-words focus:outline-none focus:ring-2 focus:ring-blue-500 scroll-mt-16"
        data-testid="diff-hunk"
        data-diff-hunk={startHunkIndex}
        tabIndex={-1}
      >
        <span data-testid="unified-diff-removed">
          {renderInline(asText(oldText) || "(empty)", sourceTitles)}
        </span>
      </pre>
    );
  }

  // modified — use the word-level diff when available, else fall back to a
  // simple two-block old/new render (correctness > prettiness: this is only
  // reached if the backend somehow sent no ops for a modified section).
  if (wordDiff.length > 0) {
    const { items } = groupWordDiffOps(wordDiff, startHunkIndex);
    return (
      <>
        <GranularityNote granularity={granularity} />
        <pre
          className="text-xs rounded border border-slate-200 p-2 mt-2 whitespace-pre-wrap break-words overflow-auto"
          data-testid="word-diff-modified"
        >
          {items.map((item, i) => {
            if (item.kind === "equal") {
              return (
                <WordOpSpan key={i} op={item.op} sourceTitles={sourceTitles} />
              );
            }
            return (
              <span
                key={i}
                data-testid="diff-hunk"
                data-diff-hunk={item.hunkIndex}
                tabIndex={-1}
                className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded scroll-mt-16 inline-block"
              >
                {item.ops.map((op, j) => (
                  <WordOpSpan key={j} op={op} sourceTitles={sourceTitles} />
                ))}
              </span>
            );
          })}
        </pre>
      </>
    );
  }

  return (
    <div
      className="space-y-1 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded scroll-mt-16"
      data-testid="diff-hunk"
      data-diff-hunk={startHunkIndex}
      tabIndex={-1}
    >
      <div data-testid="word-diff-modified">
        <pre className="text-xs bg-rose-50 text-slate-700 rounded p-2 whitespace-pre-wrap break-words">
          {renderInline(asText(oldText) || "(empty)", sourceTitles)}
        </pre>
        <pre className="text-xs bg-emerald-50 text-slate-700 rounded p-2 whitespace-pre-wrap break-words">
          {renderInline(asText(newText) || "(empty)", sourceTitles)}
        </pre>
      </div>
    </div>
  );
}

/**
 * Render the per-section diff between two versions.
 *
 * @param props - The diff to render.
 * @returns The diff-view element.
 */
export const DiffView: React.FC<DiffViewProps> = ({ diff, sourceTitles }) => {
  const sectionsWithHunkIndices = React.useMemo(() => {
    if (!diff) return [];
    let running = 1;
    return diff.sections
      .filter((s) => s.section !== "frontmatter")
      .map((s) => {
        const start = running;
        const count = countSectionHunks(s);
        running += count;
        return { section: s, startHunkIndex: start };
      });
  }, [diff]);

  const totalHunks = React.useMemo(() => {
    if (sectionsWithHunkIndices.length === 0) return 0;
    const last = sectionsWithHunkIndices[sectionsWithHunkIndices.length - 1];
    return last.startHunkIndex + countSectionHunks(last.section) - 1;
  }, [sectionsWithHunkIndices]);

  const [selectedHunk, setSelectedHunk] = React.useState<{
    diff: VersionDiff | null;
    index: number;
  }>({ diff: null, index: 0 });

  const currentHunkIndex =
    selectedHunk.diff === diff ? selectedHunk.index : totalHunks > 0 ? 1 : 0;

  const navigateToHunk = (targetIndex: number): void => {
    setSelectedHunk({ diff, index: targetIndex });
    const el = document.querySelector<HTMLElement>(
      `[data-diff-hunk="${targetIndex}"]`,
    );
    el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    el?.focus?.();
  };

  const titles = sourceTitles ?? undefined;

  if (diff === null) {
    return (
      <p className="text-sm text-slate-400 italic">
        Select two versions to compare.
      </p>
    );
  }

  return (
    <div data-testid="diff-view" className="space-y-3">
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-100 bg-white py-2.5 text-xs text-slate-500 shadow-2xs">
        <div>
          Comparing v{diff.from_version_no} → v{diff.to_version_no}
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="diff-change-counter"
            className="font-medium text-slate-600"
          >
            {totalHunks > 0
              ? `Change ${currentHunkIndex} of ${totalHunks}`
              : "0 of 0"}
          </span>
          <div className="flex items-center border border-slate-200 rounded overflow-hidden">
            <button
              type="button"
              data-testid="diff-prev-change"
              aria-label="Previous change"
              disabled={totalHunks === 0 || currentHunkIndex <= 1}
              onClick={() => navigateToHunk(currentHunkIndex - 1)}
              className="px-1.5 py-0.5 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600 transition-colors"
            >
              ▲
            </button>
            <div className="w-[1px] h-3.5 bg-slate-200" />
            <button
              type="button"
              data-testid="diff-next-change"
              aria-label="Next change"
              disabled={totalHunks === 0 || currentHunkIndex >= totalHunks}
              onClick={() => navigateToHunk(currentHunkIndex + 1)}
              className="px-1.5 py-0.5 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600 transition-colors"
            >
              ▼
            </button>
          </div>
        </div>
      </div>
      {sectionsWithHunkIndices.map(({ section: s, startHunkIndex }) => {
        const isBody = s.section === "body";
        const content = (
          <WordDiffView
            change={s.change}
            oldText={s.old}
            newText={s.new}
            wordDiff={s.word_diff}
            granularity={s.granularity}
            sourceTitles={titles}
            startHunkIndex={startHunkIndex}
          />
        );

        if (isBody) {
          return (
            <div key={s.section} data-testid="diff-section-body">
              {content}
            </div>
          );
        }

        return (
          <div
            key={s.section}
            className="rounded-lg border border-slate-200 p-3"
            data-testid={`diff-section-${s.section}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm text-slate-800">
                {s.section}
              </span>
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${CHANGE_BADGE[s.change]}`}
              >
                {s.change}
              </span>
            </div>
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default DiffView;
